import { Navigation, Phone, MapPin, ExternalLink } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { SITE } from "@/data/site";
import { Kicker, Reveal } from "./Reveal";
import { InstagramIcon } from "./Icons";
import { useOpenNow } from "./InfoStrip";

export const Location = () => {
  const { t } = useLang();
  const { open, dayIndex } = useOpenNow();
  return (
    <section id="contatti" data-testid="location-section" className="relative bg-ink text-bone overflow-hidden">
      <div className="grain-layer" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28">
        <Reveal>
          <Kicker light testid="location-kicker">{t.location.kicker}</Kicker>
          <h2 data-testid="location-title" className="font-display text-6xl sm:text-7xl lg:text-8xl mt-5 leading-[0.85]">
            {t.location.title}
          </h2>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-7" y={30}>
            <div className="relative map-frame border-4 border-bone shadow-[12px_12px_0_#D7192A] bg-coal">
              <iframe
                title="Hammer Gym Lugano - Via Fola 13"
                data-testid="location-map-embed"
                src={SITE.mapsEmbed}
                className="w-full h-[360px] sm:h-[460px] block"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <div className="absolute -top-4 left-5 bg-hammer-red text-bone font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-2 flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" /> {SITE.street}, {SITE.city}
              </div>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a href={SITE.directions} target="_blank" rel="noopener noreferrer" data-testid="get-directions-link" className="btn-brutal on-dark btn-red">
                <Navigation className="w-5 h-5" /> {t.location.directions}
              </a>
              <a href={SITE.mapsSearch} target="_blank" rel="noopener noreferrer" data-testid="open-maps-link" className="btn-brutal on-dark btn-bone">
                <ExternalLink className="w-5 h-5" /> {t.location.openMaps}
              </a>
            </div>
            <p className="mt-5 font-mono text-xs text-bone/50">{t.location.note}</p>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={0.1}>
            <div className="border-2 border-bone/25 bg-coal">
              <div className="flex items-center justify-between px-5 py-4 border-b-2 border-bone/25">
                <h3 className="font-display text-3xl">{t.location.hoursTitle}</h3>
                <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 ${open ? "bg-green-600 text-bone" : "bg-hammer-red text-bone"}`} data-testid="location-open-badge">
                  {open ? t.strip.openNow : t.strip.closedNow}
                </span>
              </div>
              <ul data-testid="hours-list">
                {t.location.days.map((d, i) => (
                  <li
                    key={d}
                    className={`flex items-center justify-between px-5 py-3 font-mono text-sm border-b border-bone/10 last:border-b-0 ${
                      i === dayIndex ? "bg-hammer-red text-bone" : "text-bone/80"
                    }`}
                    data-testid={`hours-row-${i}`}
                  >
                    <span className="uppercase tracking-wider">
                      {d} {i === dayIndex && <span className="ml-2 text-[10px] opacity-80">← {t.location.today}</span>}
                    </span>
                    <span>06:00 — 22:00</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 grid gap-4">
              <a href={SITE.tel} data-testid="contact-phone-link" className="group flex items-center justify-between border-2 border-bone/25 hover:border-hammer-red hover:bg-hammer-red transition-colors px-5 py-4">
                <span className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-hammer-red group-hover:text-bone" />
                  <span className="font-display text-3xl leading-none pt-1">{SITE.phoneDisplay}</span>
                </span>
                <span className="font-mono text-[11px] uppercase tracking-widest">{t.location.call}</span>
              </a>
              <a href={SITE.instagram} target="_blank" rel="noopener noreferrer" data-testid="instagram-link" className="group flex items-center justify-between border-2 border-bone/25 hover:border-hammer-red hover:bg-hammer-red transition-colors px-5 py-4">
                <span className="flex items-center gap-3">
                  <InstagramIcon className="w-5 h-5 text-hammer-red group-hover:text-bone" />
                  <span className="font-display text-3xl leading-none pt-1">{SITE.instagramHandle}</span>
                </span>
                <span className="font-mono text-[11px] uppercase tracking-widest">Instagram</span>
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
